import React, { useState, useMemo } from "react";
import CompanyList from "./CompanyList";
import { ArrowUpRight, ArrowDownRight, ChevronDown } from "lucide-react";

export default function SectorTrendsExplorer({ rows, isLatest, monthLabel }) {
  const sectors = useMemo(() => {
    const set = new Set();
    rows.forEach(r => {
      const status = (r.monthly_status || "").toLowerCase();
      if ((status === "investing" || status === "withdrawing") && r.sector) {
        set.add(r.sector);
      }
    });
    return Array.from(set).sort();
  }, [rows]);

  const [selected, setSelected] = useState("");
  const data = useMemo(() => {
    if (!selected) return null;
    const sectorRows = rows.filter(r => r.sector === selected);
    const investingRows = [];
    const withdrawingRows = [];
    const allCompanies = new Set();
    const investingSet = new Set();
    const withdrawingSet = new Set();
    sectorRows.forEach(r => {
      allCompanies.add(r.company);
      const status = (r.monthly_status || "").toLowerCase();
      if (status === "investing") {
        investingRows.push(r);
        investingSet.add(r.company);
      } else if (status === "withdrawing") {
        withdrawingRows.push(r);
        withdrawingSet.add(r.company);
      }
    });
    const uniqueInvestingRows = Array.from(
      new Map(investingRows.map(r => [r.company, r])).values()
    );
    const uniqueWithdrawingRows = Array.from(
      new Map(withdrawingRows.map(r => [r.company, r])).values()
    );
    return {
      investingRows: uniqueInvestingRows,
      withdrawingRows: uniqueWithdrawingRows,
      total: allCompanies.size,
      investingCount: investingSet.size,
      withdrawingCount: withdrawingSet.size,
    };
  }, [rows, selected]);

  return (
    <div className="sector-trends-explorer">
      <h2>Sector trends explorer</h2>
      {!selected && (
        <p className="sector-placeholder-text">
          {`Select a sector to see which companies ${
            isLatest
              ? "have invested in or withdrawn from the U.S. in the last 30 days"
              : `invested in or withdrew from the U.S. in ${monthLabel}`
          }.`}
        </p>
      )}
        <div className="sector-select-wrapper">
          <select
            className="gold-button sector-select"
            value={selected}
            onChange={e => setSelected(e.target.value)}
          >
            <option value="">Select a sector</option>
            {!sectors.includes(selected) && selected && (
              <option value={selected}>{selected}</option>
            )}
            {sectors.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <ChevronDown className="chevron" size={16} />
        </div>
      {selected && (
        <div className="sector-trends-details">
          {(() => {
            const timeframe = isLatest ? "in the last 30 days" : `in ${monthLabel}`;
            const investPhrase = isLatest
              ? `${data.investingCount} ${data.investingCount === 1 ? "has" : "have"} invested in`
              : `${data.investingCount} invested`;
            const withdrawPhrase = isLatest
              ? `${data.withdrawingCount} ${data.withdrawingCount === 1 ? "has" : "have"} withdrawn from the U.S.`
              : `${data.withdrawingCount} withdrew from the U.S.`;
            return (
              <p>
                Out of {data.total} {selected} companies, {investPhrase} and {withdrawPhrase} {timeframe}.
              </p>
            );
          })()}
          {data.investingRows.length > 0 && (
            <div>
              <p>
                {`Companies that ${isLatest ? "have " : ""}invested`}
                <ArrowUpRight
                  className="status-icon"
                  size={16}
                  style={{ display: "inline", margin: "0 0 0 4px" }}
                />
              </p>
              <CompanyList rows={data.investingRows} expandable={false} />
            </div>
          )}
          {data.withdrawingRows.length > 0 && (
            <div>
              <p>
                {`Companies that ${isLatest ? "have withdrawn" : "withdrew"}`}
                <ArrowDownRight
                  className="status-icon"
                  size={16}
                  style={{ display: "inline", margin: "0 0 0 4px" }}
                />
              </p>
              <CompanyList rows={data.withdrawingRows} expandable={false} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
